import {
	CurrentInteractionState,
	useCurrentInteractionStore,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import { useMemo } from "react";
import {
	CallSessionState,
	useCallSessionStore,
} from "@/common/stores/nonpersistingStores/callSession";
import { AppCacheState, CacheKey, useAppCacheStore } from "@/common/stores/persistingStores/app";
import { WrapUpState, useWrapUpStore } from "@/common/stores/nonpersistingStores/wrapup";
import { InteractionType } from "@/common/types";
import { InteractionDirection } from "@/repository/models";

export const useVariableTextVisibility = (): boolean => {
	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.preview ? state.preview : state.active
	);

	const callSession = useCallSessionStore(
		(state: CallSessionState) => state.callSession
	);

	const appStoreCache = useAppCacheStore((state: AppCacheState) => state.cache);

	const wrapUp = useWrapUpStore((state: WrapUpState) => state.wrapUp);

	const isCallVariableTextVisible = useMemo(() => {
		const isCallCurrentInteraction = currentInteraction?.id === callSession?.id;
		const isCallWrapUp =
			wrapUp !== null && wrapUp.mediaId === currentInteraction?.id;

		const variableTextExists =
			callSession?.activeVariableText ||
			currentInteraction?.activeVariableText ||
			appStoreCache[CacheKey.SessionVariableText];

		return (isCallCurrentInteraction || isCallWrapUp) && variableTextExists;
	}, [currentInteraction, callSession, wrapUp, appStoreCache]);

	const isMessagingVariableTextVisible = useMemo(() => {
		const isCallWrapUp =
			wrapUp !== null && wrapUp.mediaId === currentInteraction?.id;

		const messagingVariableTextArray =
			appStoreCache[CacheKey.MessagingVariableText];

		const cacheValue = messagingVariableTextArray
			? messagingVariableTextArray.find(
					(x: any) => x.key === currentInteraction?.id
				)
			: undefined;

		const variableTextExists =
			currentInteraction?.activeVariableText || cacheValue?.value;

		const isOutbound =
			currentInteraction?.direction === InteractionDirection.Outbound;

		return (
			(currentInteraction || isCallWrapUp) && variableTextExists && !isOutbound
		);
	}, [currentInteraction, callSession, wrapUp, appStoreCache]);

	const isVariableTextVisible = useMemo(() => {
		return currentInteraction?.type === InteractionType.Call
			? isCallVariableTextVisible
			: isMessagingVariableTextVisible;
	}, [currentInteraction, callSession, wrapUp, appStoreCache]);

	return isVariableTextVisible;
};
